import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Plus, Trash2, User, Calendar, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StableInput from './stable-input';

const SpecialistsVisitsForm = ({ data, onChange, onSave, isLoading, readonly = false }) => {
  const [formData, setFormData] = useState([]);

  const formDataRef = useRef(formData);
  const updateTimeoutRef = useRef(null);
  formDataRef.current = formData;

  const debouncedUpdate = useCallback((updatedData) => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(updatedData);
      }
    }, 100);
  }, [onChange]);

  useEffect(() => { 
    if (data && Array.isArray(data)) {
      setFormData(data);
    } else if (data) {
      setFormData(data.specialistsVisits || []);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleAddSpecialistVisit = useCallback(() => {
    const newVisit = {
      id: Date.now(),
      date: '',
      specialty: '',
      specialistName: '',
      details: ''
    };
    
    setFormData(prev => {
      const updatedData = [...prev, newVisit];
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const handleVisitChange = useCallback((id, field, value) => {
    setFormData(prev => {
      const updatedData = prev.map(visit => 
        visit.id === id ? { ...visit, [field]: value } : visit
      );
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const handleDeleteVisit = useCallback((id) => {
    setFormData(prev => {
      const updatedData = prev.filter(visit => visit.id !== id);
      debouncedUpdate(updatedData);
      return updatedData;
    });
  }, [debouncedUpdate]);

  const validateForm = () => {
    const errors = [];
    
    formData.forEach((visit, index) => {
      if (visit.date && !visit.specialty) {
        errors.push(`Visit ${index + 1}: Specialty is required when date is provided`);
      }
      if (visit.specialty && !visit.date) {
        errors.push(`Visit ${index + 1}: Date is required when specialty is provided`);
      }
      if (visit.specialistName && !visit.specialty) {
        errors.push(`Visit ${index + 1}: Specialty is required when specialist name is provided`);
      }
      if (visit.details && !visit.specialty) {
        errors.push(`Visit ${index + 1}: Specialty is required when visit details are provided`);
      }
    });
    
    return errors;
  };

  const hasData = () => {
    return formData.length > 0 && formData.some(visit => 
      visit.specialty || visit.date || visit.specialistName || visit.details
    );
  };

  const handleSave = async () => {
    if (!hasData()) {
      toast.error('Please add at least one specialist visit before saving');
      return;
    }

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    if (onSave) {
      await onSave(formData);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-purple-500" />
            Specialists Visits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4"> 
          <div className="hidden xl:grid grid-cols-12 gap-4 font-medium text-sm text-gray-600 border-b pb-2">
            <div className="xl:col-span-1">Sl No</div>
            <div className="xl:col-span-2">Date Of Visit (MM/DD/YY)</div>
            <div className="xl:col-span-2">Name of Specialty</div>
            <div className="xl:col-span-2">Last Name of Specialist</div>
            <div className="xl:col-span-4">Details of Visit</div>
            <div className="xl:col-span-1">Action</div>
          </div>
          {formData.map((visit, index) => (
            <div key={visit.id} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 xl:grid-cols-12 xl:gap-4 xl:rounded-none xl:border-0 xl:p-0 items-start">
              <div data-label="Sl No" className="xl:col-span-1 text-sm text-gray-500 xl:pt-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                {index + 1}
              </div>
              <div data-label="Date of Visit (MM/DD/YY)" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <StableInput
                  placeholder="03/10/2024"
                  value={visit.date}
                  onChange={(value) => handleVisitChange(visit.id, 'date', value)}
                  readonly={readonly}
                />
              </div>
              <div data-label="Name of Specialty" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <StableInput
                  placeholder="Cardiology"
                  value={visit.specialty}
                  onChange={(value) => handleVisitChange(visit.id, 'specialty', value)}
                  readonly={readonly}
                />
              </div>
              <div data-label="Last Name of Specialist" className="xl:col-span-2 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <StableInput
                  placeholder="Dr. Smith"
                  value={visit.specialistName}
                  onChange={(value) => handleVisitChange(visit.id, 'specialistName', value)}
                  readonly={readonly}
                />
              </div>
              <div data-label="Details of Visit" className="xl:col-span-4 before:content-[attr(data-label)] before:block before:mb-1 before:text-sm before:font-semibold before:text-gray-500 xl:before:hidden">
                <Textarea
                  placeholder="Follow-up for hypertension management, BP well controlled"
                  value={visit.details}
                  onChange={(e) => handleVisitChange(visit.id, 'details', e.target.value)}
                  className="text-sm min-h-[40px] resize-none"
                  rows={2}
                  readOnly={readonly}
                />
              </div>
              <div className="xl:col-span-1 xl:pt-2 flex justify-end xl:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteVisit(visit.id)}
                  disabled={readonly}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))} 
          <Button
            variant="outline"
            onClick={handleAddSpecialistVisit}
            disabled={readonly}
            className="w-full border-dashed border-2 border-gray-300 hover:border-gray-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Specialist Visit
          </Button>
        </CardContent>
      </Card>
      {!readonly && onSave && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isLoading || readonly || !hasData()}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Specialists Visits
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default React.memo(SpecialistsVisitsForm);
